import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-oneportal";
import { formatRegionLine, type ParsedAddress } from "@/utils/address";
import styles from "./AddressVerificationModal.module.css";

export interface AddressVerificationItem {
  locationId: string;
  original: ParsedAddress;
  suggested: ParsedAddress | null;
}

export interface AddressVerificationModalProps {
  items: AddressVerificationItem[];
  onCancel: () => void;
  onComplete: (resolved: Record<string, ParsedAddress>) => void;
}

type AddressChoice = "suggested" | "original";

const defaultChoice = (item: AddressVerificationItem): AddressChoice =>
  item.suggested ? "suggested" : "original";

/**
 * Walks the user through every location whose address failed verification,
 * one per step, letting them keep what they typed or take Google's suggestion.
 */
export const AddressVerificationModal: React.FC<AddressVerificationModalProps> = ({
  items,
  onCancel,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [choices, setChoices] = useState<Record<string, AddressChoice>>(() =>
    Object.fromEntries(items.map((item) => [item.locationId, defaultChoice(item)])),
  );
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCancelRef = useRef(onCancel);

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const backdrop = dialogRef.current;
    const background = backdrop?.parentElement;
    const inertSiblings = background
      ? Array.from(background.children)
        .filter((element): element is HTMLElement =>
          element instanceof HTMLElement && element !== backdrop,
        )
        .map((element) => ({ element, wasInert: element.inert }))
      : [];

    inertSiblings.forEach(({ element }) => {
      element.inert = true;
    });
    backdrop?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancelRef.current();
        return;
      }

      if (event.key !== "Tab" || !backdrop) return;

      const focusable = Array.from(
        backdrop.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.inert && element.getAttribute("aria-hidden") !== "true");
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) {
        event.preventDefault();
      } else if (event.shiftKey && (document.activeElement === first || !backdrop.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !backdrop.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      inertSiblings.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      previouslyFocused?.focus();
    };
  }, []);

  const item = items[stepIndex];
  const isLastStep = stepIndex === items.length - 1;

  if (!item) return null;

  const selected = choices[item.locationId] ?? defaultChoice(item);

  const select = (choice: AddressChoice) =>
    setChoices((prev) => ({ ...prev, [item.locationId]: choice }));

  const handleNext = () => {
    if (!isLastStep) {
      setStepIndex((prev) => prev + 1);
      return;
    }

    onComplete(
      Object.fromEntries(
        items.map((entry) => [
          entry.locationId,
          (choices[entry.locationId] ?? defaultChoice(entry)) === "suggested" &&
            entry.suggested
            ? entry.suggested
            : entry.original,
        ]),
      ),
    );
  };

  const renderOption = (
    choice: AddressChoice,
    label: string,
    address: ParsedAddress,
  ) => (
    <label
      className={`${styles.option} ${selected === choice ? styles.optionSelected : ""}`}
      data-testid={`address-verification-${choice}`}
    >
      <input
        type="radio"
        className={styles.radio}
        name={`address-choice-${item.locationId}`}
        value={choice}
        checked={selected === choice}
        onChange={() => select(choice)}
      />
      <span className={styles.optionBody}>
        <span className={styles.optionLabel}>{label}</span>
        <span className={styles.optionLine}>{address.addressLine1}</span>
        <span className={styles.optionLine}>{formatRegionLine(address)}</span>
      </span>
    </label>
  );

  return (
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="address-verification-title"
      data-testid="address-verification-modal"
      ref={dialogRef}
      tabIndex={-1}
    >
      <div className={styles.modal}>
        <h2 id="address-verification-title" className={styles.title}>
          {t("onboardingTour.locationStep.addressVerification.title")}
        </h2>
        <p className={styles.message}>
          {t("onboardingTour.locationStep.addressVerification.message")}
        </p>
        <p className={styles.chooseLabel}>
          {t("onboardingTour.locationStep.addressVerification.chooseOne")}
        </p>

        <div className={styles.options}>
          {item.suggested &&
            renderOption(
              "suggested",
              t("onboardingTour.locationStep.addressVerification.suggestedAddress"),
              item.suggested,
            )}
          {renderOption(
            "original",
            t("onboardingTour.locationStep.addressVerification.originalAddress"),
            item.original,
          )}
        </div>

        {items.length > 1 && (
          <div className={styles.dots} data-testid="address-verification-dots">
            {items.map((entry, index) => (
              <span
                key={entry.locationId}
                className={`${styles.dot} ${index === stepIndex ? styles.dotActive : ""}`}
              />
            ))}
          </div>
        )}

        <div className={styles.actions}>
          <Button
            type="button"
            variant="highlightTertiary"
            className={styles.cancelButton}
            onClick={onCancel}
            data-testid="address-verification-cancel"
          >
            {t("onboardingTour.locationStep.addressVerification.cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            className={styles.nextButton}
            onClick={handleNext}
            data-testid="address-verification-next"
          >
            {isLastStep
              ? t("onboardingTour.locationStep.addressVerification.finish")
              : t("onboardingTour.locationStep.addressVerification.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddressVerificationModal;
